import { type Card, SUIT_SYMBOL, SUIT_COLOR } from '@durak/shared';
import clsx from 'clsx';
import { motion, type MotionProps } from 'framer-motion';
import { forwardRef } from 'react';
import { FaceCardArt } from './FaceCardArt';

const FACE_RANKS = new Set(['J', 'Q', 'K']);

interface Props extends MotionProps {
  card?: Card | null;
  faceDown?: boolean;
  selected?: boolean;
  selectable?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  small?: boolean;
  layoutId?: string;
}

export const PlayingCard = forwardRef<HTMLDivElement, Props>(function PlayingCard(
  { card, faceDown, selected, selectable, disabled, onClick, className, small, layoutId, ...motionProps },
  ref,
) {
  const layout = layoutId ?? (card ? `card-${card.id}` : undefined);
  if (faceDown || !card) {
    return (
      <motion.div
        ref={ref}
        layoutId={layout}
        className={clsx('playing-card face-down', small && 'scale-90 origin-center', className)}
        onClick={onClick}
        aria-label="Face-down card"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'tween', duration: 0.18, ease: 'easeOut' }}
        {...motionProps}
      />
    );
  }
  const color = SUIT_COLOR[card.suit];
  const sym = SUIT_SYMBOL[card.suit];
  return (
    <motion.div
      ref={ref}
      layoutId={layout}
      className={clsx(
        'playing-card',
        color === 'red' && 'red',
        selectable && !disabled && 'selectable',
        selected && 'selected',
        disabled && 'disabled',
        small && 'scale-90 origin-center',
        className,
      )}
      onClick={!disabled ? onClick : undefined}
      role="button"
      aria-label={`${card.rank} of ${card.suit}`}
      initial={{ opacity: 0, y: 8, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, y: -10 }}
      transition={{ type: 'spring', stiffness: 320, damping: 26 }}
      whileTap={!disabled && selectable ? { scale: 0.96 } : undefined}
      {...motionProps}
    >
      <span className="playing-card-corner playing-card-corner-tl">
        <span className="playing-card-rank" data-rank={card.rank}>{card.rank}</span>
        <span className="playing-card-suit">{sym}</span>
      </span>
      {FACE_RANKS.has(card.rank) ? (
        <span className="playing-card-face">
          <FaceCardArt rank={card.rank as 'J' | 'Q' | 'K'} suit={card.suit} />
        </span>
      ) : (
        <span className="playing-card-center">{sym}</span>
      )}
      <span className="playing-card-corner playing-card-corner-br">
        <span className="playing-card-rank" data-rank={card.rank}>{card.rank}</span>
        <span className="playing-card-suit">{sym}</span>
      </span>
    </motion.div>
  );
});
